import { useState, useEffect, useRef } from 'react';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import toast from 'react-hot-toast';

interface RfqPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  transportId: number;
}

export const RfqPreviewModal = ({
  isOpen,
  onClose,
  transportId
}: RfqPreviewModalProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [htmlMessage, setHtmlMessage] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && transportId) {
      loadRfqPreview();
    }
  }, [isOpen, transportId]);

  const loadRfqPreview = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/v1/transport/${transportId}/rfq-preview`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to load RFQ preview');

      const data = await response.json();
      setHtmlMessage(data.message);
    } catch (error) {
      console.error('Failed to load RFQ preview:', error);
      toast.error('Nie udało się załadować podglądu wiadomości');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Get edited HTML from contentEditable div
      const editedHtml = contentRef.current?.innerHTML || htmlMessage;

      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/v1/transport/${transportId}/rfq-message`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: editedHtml })
      });

      if (!response.ok) throw new Error('Failed to save message');

      toast.success('Wiadomość została zapisana');
      setHtmlMessage(editedHtml);
    } catch (error) {
      console.error('Failed to save RFQ message:', error);
      toast.error('Nie udało się zapisać wiadomości');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Podgląd wiadomości RFQ" size="xl">
        <div className="flex justify-center items-center py-12">
          <div className="text-gray-500">Ładowanie podglądu...</div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Podgląd i edycja wiadomości RFQ" size="full">
      <div className="space-y-4 h-full flex flex-col">

        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex-shrink-0">
          <p className="text-sm text-blue-800">
            <strong>Wskazówka:</strong> Kliknij na dowolny tekst w wiadomości, aby go edytować bezpośrednio.
          </p>
        </div>

        {/* Editable content - no scroll, show full content */}
        <div className="border-2 border-gray-200 rounded-lg overflow-auto flex-1">
          <div
            ref={contentRef}
            contentEditable={true}
            suppressContentEditableWarning={true}
            dangerouslySetInnerHTML={{ __html: htmlMessage }}
            className="p-6 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2 pt-4 border-t flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Zamknij
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {saving ? 'Zapisywanie...' : 'Zapisz zmiany'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
